import { LuTags } from '@react-icons/all-files/lu/LuTags'
import React, { useCallback, useState } from 'react'

import { isNativeTagsEnabled } from '@disco/state-management/selectors/user'
import mixpanel from '@disco/api/mixpanel'
import NativeTagsPopover from 'components/common/native-tags-popover'
import Button from 'components/ui/button'
import Tooltip from 'components/ui/tooltip'

import { useSelector } from 'hooks/use-redux'

type Props = {
  assignedTags: Record<string, any>[]
  buttonSize?: 'default' | 'compact'
  showLabel?: boolean
  trackIds: number[]
  mixpanelContext?:
    | 'Explorer'
    | 'Track pane'
    | 'Track pane body'
    | 'Shortlist'
    | 'Player'
    | 'Browse'
    | 'Starred'
    | 'Inbox'
    | 'Channel'
    | 'Similar tracks'
    | 'Search results'
}

/**
 * Native tags menu component that provides tag management interface
 * Shows button to open tag popover with support for readonly mode
 * @param assignedTags - Currently assigned tags to tracks
 * @param buttonSize - Size of the menu button ('default' or 'compact')
 * @param showLabel - Whether to show button label text
 * @param trackIds - Array of track IDs to apply tags to
 * @param mixpanelContext - Context surface for the 'Click icon' Mixpanel event
 */
function NativeTagsMenu({
  assignedTags,
  buttonSize,
  showLabel,
  trackIds,
  mixpanelContext,
}: Props): null | React.ReactNode {
  const nativeTagsEnabled: boolean = useSelector(isNativeTagsEnabled)
  const isNativeTagsReadonly: boolean = useSelector(
    state =>
      state.user.business?.private_metadata.is_native_tags_readonly ?? false,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const toggle = useCallback(() => {
    if (!menuOpen) {
      mixpanel.sendPureEvent('Click icon', {
        'Track ID': trackIds.length === 1 ? trackIds[0] : undefined,
        Type: 'Track',
        Icon: 'Tags',
        Context: mixpanelContext,
      })
    }
    setMenuOpen(m => !m)
  }, [menuOpen, trackIds, mixpanelContext])
  const close = useCallback(() => {
    setMenuOpen(false)
  }, [])
  if (!nativeTagsEnabled) return null
  const label = isNativeTagsReadonly ? (
    <span>Tag editing is not enabled for this DISCO.</span>
  ) : (
    <span>Add track tags</span>
  )
  let button

  if (showLabel) {
    button = (
      <Tooltip content={isNativeTagsReadonly ? label : null}>
        <Button
          appearance="link"
          caption="Add track tags"
          compact={buttonSize === 'compact'}
          disabled={isNativeTagsReadonly}
          iconNode={<LuTags size={22} />}
          onClick={toggle}
        />
      </Tooltip>
    )
  } else {
    button = (
      <Tooltip content={label}>
        <Button
          appearance="icon"
          compact={buttonSize === 'compact'}
          disabled={isNativeTagsReadonly}
          iconNode={<LuTags size={22} />}
          onClick={toggle}
        />
      </Tooltip>
    )
  }

  return (
    <>
      {button}
      {menuOpen && (
        <NativeTagsPopover
          trackIds={trackIds}
          assignedTags={assignedTags}
          onClose={close}
        />
      )}
    </>
  )
}

export default NativeTagsMenu
